import { useMemo } from 'react';
import { EditSessionSettings } from './EditSessionSettings';
import { EditSessionPublic } from './EditSessionPublic';
import { EditSessionTarget } from './EditSessionTarget';
import { EditSessionScreening } from './EditSessionScreening';
import { EditSessionSchedule } from './EditSessionSchedule';
import { EditSessionReview } from './EditSessionReview';
import {
  RecruitmentBy,
  Session,
  StudyType,
  useCapabilities,
  useIsAdmin,
  usePlan,
  useRecruitment,
} from '@app/api';
import { EditSessionLinkConfiguration } from 'pages/Dashboard/EditSession/EditSessionLinkConfiguration';
import { getMaxNumberOfQuestions } from 'lib/billing';
import { EditSessionAdmin } from './EditSessionAdmin';
import { useTranslation } from 'react-i18next';

interface OnlySessionProps {
  session: Session;
}

export enum EditSessionStep {
  REVIEW = 'review',
  SCHEDULE = 'schedule',
  SCREENING_FORM = 'screening',
  TARGETING = 'targeting',
  PUBLIC = 'public',
  SETTINGS = 'settings',
  LINK_CONFIGURATION = 'link-configuration',
  ADMIN = 'admin',
}

export const useEditSessionSteps = (session?: Session) => {
  const { data: recruitment } = useRecruitment(session?.recruitmentId);
  const { t } = useTranslation();

  const editSessionSteps = {
    [EditSessionStep.SETTINGS]: {
      path: 'settings',
      link: t('editSession.nav.settings'),
      name: EditSessionStep.SETTINGS,
      getElement: (props: OnlySessionProps) => (
        <EditSessionSettings {...props} />
      ),
    },
    [EditSessionStep.PUBLIC]: {
      path: 'public',
      link: t('editSession.nav.public'),
      name: EditSessionStep.PUBLIC,
      getElement: (props: OnlySessionProps) => <EditSessionPublic {...props} />,
    },
    [EditSessionStep.TARGETING]: {
      path: 'targeting',
      link: t('editSession.nav.targeting'),
      name: EditSessionStep.TARGETING,
      getElement: (props: OnlySessionProps) => <EditSessionTarget {...props} />,
    },
    [EditSessionStep.SCREENING_FORM]: {
      path: 'screening-form',
      link: t('editSession.nav.screeningForm'),
      name: EditSessionStep.SCREENING_FORM,
      getElement: (props: OnlySessionProps) => (
        <EditSessionScreening {...props} />
      ),
    },
    [EditSessionStep.SCHEDULE]: {
      path: 'schedule',
      link: t('editSession.nav.schedule'),
      name: EditSessionStep.SCHEDULE,
      getElement: (props: OnlySessionProps) => (
        <EditSessionSchedule {...props} />
      ),
    },
    [EditSessionStep.REVIEW]: {
      path: 'review',
      link: t('editSession.nav.review'),
      name: EditSessionStep.REVIEW,
      getElement: (props: OnlySessionProps) => <EditSessionReview {...props} />,
    },
    [EditSessionStep.LINK_CONFIGURATION]: {
      path: 'link-configuration',
      link: t('editSession.nav.linkConfiguration'),
      name: EditSessionStep.LINK_CONFIGURATION,
      getElement: (props: OnlySessionProps) => (
        <EditSessionLinkConfiguration {...props} />
      ),
    },
    [EditSessionStep.ADMIN]: {
      path: 'admin',
      link: t('editSession.nav.admin'),
      name: EditSessionStep.ADMIN,
      getElement: (props: OnlySessionProps) => <EditSessionAdmin {...props} />,
    },
  };

  const capabilities = useCapabilities(
    session?.planId,
    session?.organizationId,
  );
  const { data: plan } = usePlan(session?.planId);
  const maxQuestions = getMaxNumberOfQuestions(plan?.products);
  const isAdmin = useIsAdmin();

  return useMemo(() => {
    if (!recruitment || !capabilities || !session) {
      return {
        steps: [
          EditSessionStep.SETTINGS,
          EditSessionStep.PUBLIC,
          EditSessionStep.TARGETING,
          EditSessionStep.SCREENING_FORM,
          EditSessionStep.SCHEDULE,
          EditSessionStep.LINK_CONFIGURATION,
          EditSessionStep.REVIEW,
        ].map((step) => editSessionSteps[step]),
        isLoading: true,
      };
    }
    return {
      steps: (
        [
          EditSessionStep.SETTINGS,
          EditSessionStep.PUBLIC,
          recruitment.recruitmentBy !== RecruitmentBy.CLIENT
            ? EditSessionStep.TARGETING
            : undefined,
          (maxQuestions === 0 && !isAdmin) || !capabilities?.screeningForm
            ? undefined
            : EditSessionStep.SCREENING_FORM,
          session?.studyType === StudyType.MODERATED
            ? EditSessionStep.SCHEDULE
            : EditSessionStep.LINK_CONFIGURATION,
          EditSessionStep.REVIEW,
        ].filter(Boolean) as EditSessionStep[]
      ).map((step) => editSessionSteps[step]),
      isLoading: false,
    };
  }, [recruitment, session, capabilities, maxQuestions]);
};
